import { useState, useEffect, useCallback } from 'react';
import { PageHeader } from '@wajeez-web/components/layouts';
import { Calendar } from 'antd';
import moment from 'moment';
import {
  useContentSelector,
  DailyContentActions,
} from '@wajeez-web/stores/content';
import { useAppDispatch } from '@wajeez-web/stores/base';
import dayjs from 'dayjs';
import { InputSelect, OptionType } from '@wajeez-web/components/ui';
import { convertLocalDateTimeToZero } from '@wajeez-web/core/utils';

import DailyContentModal from '../components/DailyContentModal';
import { useAuthSelector } from '@wajeez-web/stores/auth';

const getFirstDateAndLastDateOnThePanel = (date: dayjs.Dayjs) => {
  const firstDate = dayjs(date).startOf('month');
  const lastDate = dayjs(date).endOf('month');

  const firstDateDay = firstDate.day();

  firstDate.subtract(firstDateDay, 'days');
  lastDate.add(42 - Number(lastDate.format('DD')) - firstDateDay, 'days');

  return {
    firstDate,
    lastDate,
  };
};

const DailyContentPage = () => {
  const dispatch = useAppDispatch();

  const [addDailyContentModalState, setAddDailyContentModalState] = useState<{
    open: boolean;
    selectedDate: string;
  }>({ open: false, selectedDate: '' });
  const [selectedContentType, setSelectedContentType] = useState<
    string | undefined
  >('BOOK');
  const [selectedStartingDate, setSelectedStartingDate] = useState<string>(
    () => {
      const { firstDate } = getFirstDateAndLastDateOnThePanel(
        dayjs(new Date())
      );

      return convertLocalDateTimeToZero(firstDate);
    }
  );

  const permissions = useAuthSelector(({ auth }) => auth.permission);

  const { data } = useContentSelector((s) => s.dailyContent);
  const contentEntityTypes = useContentSelector(
    ({ contentEntityType }) => contentEntityType.data
  );

  const mapContentEntityTypes = contentEntityTypes?.map((c) => {
    return { value: c.name, label: c.displayNamePlural };
  });

  const getDailyContent = useCallback(() => {
    dispatch(
      DailyContentActions.getAllDailyBooksAsync({
        startingFrom: selectedStartingDate,
        typeName: selectedContentType,
      })
    );
  }, [selectedContentType, selectedStartingDate, dispatch]);

  useEffect(() => {
    if (selectedContentType && selectedStartingDate) {
      getDailyContent();
    }
  }, [selectedContentType, selectedStartingDate, getDailyContent]);

  const handlePanelChange = (date: dayjs.Dayjs) => {
    const { firstDate } = getFirstDateAndLastDateOnThePanel(
      dayjs(date || new Date())
    );

    setSelectedStartingDate(convertLocalDateTimeToZero(firstDate));
  };

  return (
    <div className="h-full pe-4">
      {addDailyContentModalState.open && (
        // TODO :show selected content in the modal
        <DailyContentModal
          onSubmit={async (data) => {
            const res = data?.date
              ? await dispatch(
                  DailyContentActions.addNewDailyContentToSpicificDateAsync({
                    contentEntityId: data.contentEntityId,

                    date: data.date,
                  })
                )
              : await dispatch(
                  DailyContentActions.addNewDailyContentToSpicificDateAsync({
                    contentEntityId: data?.contentEntityId,
                    date: data?.date,
                  })
                );
            if (res.meta?.requestStatus === 'fulfilled') {
              setAddDailyContentModalState({
                open: false,
                selectedDate: '',
              });
              getDailyContent();
            }
          }}
          isOpen={addDailyContentModalState.open}
          onClose={() => {
            setAddDailyContentModalState({ open: false, selectedDate: '' });
          }}
          date={addDailyContentModalState.selectedDate}
          selectedContentType={selectedContentType}
        />
      )}
      <div className="relative w-full">
        <PageHeader
          title="Free Content"
          breadCrumbsLinks={[{ title: 'free-content' }]}
        />
        <div className="absolute top-4 left-3 flex justify-center items-center space-s-2">
          <div className="text-base text-gray-700 font-medium">النوع</div>
          <div className="w-[180px]">
            <InputSelect
              name="daily-book-type"
              options={mapContentEntityTypes || []}
              onChange={(name, option) => {
                const optionTyped = option as OptionType;

                setSelectedContentType(optionTyped['value']);
              }}
              value={mapContentEntityTypes?.find(
                (c) => c.value === selectedContentType
              )}
            />
          </div>
        </div>
      </div>
      <Calendar
        onSelect={(date) => {
          const today = new Date();
          if (
            date >= moment(today).startOf('day') &&
            permissions?.includes('83.0')
          ) {
            setAddDailyContentModalState({
              open: true,
              selectedDate: convertLocalDateTimeToZero(date),
            });
          }
        }}
        onPanelChange={handlePanelChange}
        dateCellRender={(value) => {
          return (
            <div className="text-center">
              {data.find(
                (item) =>
                  dayjs(item.date).format('YYYY-MM-DDDD') ===
                  value.format('YYYY-MM-DDDD')
              )?.title || ''}
            </div>
          );
        }}
      />
    </div>
  );
};

export default DailyContentPage;
